import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadResume, getUserResumes, getCredits } from '../../../../services/resume.service';
import { toast } from 'react-hot-toast';
import ConfirmModal from '../common/ConfirmModal';
import CreditBadge from '../common/CreditBadge';

const DashboardStep = ({ onNavigate, onDataUpdate }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [confirmConfig, setConfirmConfig] = useState({ isOpen: false });
  const [credits, setCredits] = useState(100);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const userId = localStorage.getItem('user_id');
      const category = localStorage.getItem('category');

      if (!userId || !category) return;

      // Fetch credits
      const creditData = await getCredits(userId, category);
      if (creditData.success) {
        setCredits(creditData.credits);
      }

      const data = await getUserResumes(userId, category);
      if (data.success && data.resumes) {
        // Map backend fields to frontend session format
        const mappedSessions = data.resumes.map(r => ({
          id: r.id,
          filename: r.filename || r.resume_filename || 'Resume',
          uploadedAt: r.created_at || new Date().toISOString(),
          status: r.status || 'uploaded',
          uploadedFileRef: {
            ...r,
            filename: r.filename || r.resume_filename || (r.current_resume_url ? r.current_resume_url.replace('https://storage.googleapis.com/accredian-assets/', '') : 'Resume')
          },
          targetRole: r.target_role,
          jobDescription: r.job_description,
          result: r.parsed_data ? (typeof r.parsed_data === 'string' ? JSON.parse(r.parsed_data) : r.parsed_data) : null,
          type: r.type || 'create',
          step: r.step,
          stage: r.stage || 1
        }));
        setSessions(mappedSessions);
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
      // toast.error('Failed to load your resumes');
    }
  };

  const saveSessions = (newSessions) => {
    setSessions(newSessions);
    // Removed localStorage sync as we are now using API-driven fetching
  };

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;

    if (selectedFile.type === 'application/pdf' || selectedFile.name.toLowerCase().endsWith('.pdf')) {
      setFile(selectedFile);
    } else {
      toast.error('Only PDF files are allowed. Please upload a valid PDF document.', {
        position: "top-right"
      });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (sessions.length >= 5 || credits === 0) return;
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  const handleUpload = async () => {
    if (!file) return;
    try {
      setUploading(true);
      const userId = localStorage.getItem('user_id');
      const category = localStorage.getItem('category');
      console.log('Uploading resume with metadata:', { userId, category });
      const res = await uploadResume(file, userId, category);

      // Refresh sessions and credits from API after successful upload
      await loadSessions();

      const normalizedRes = {
        ...res,
        filename: res.file?.filename || res.filename
      };

      setUploadedFile(normalizedRes);
      toast.success('Resume uploaded successfully!');

      onNavigate('review', { 
        uploadedFile: { ...normalizedRes, id: res.id, stage: res.stage },
        stage: res.stage,
        isFresh: true
      });
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteSession = (id) => {
    setConfirmConfig({
      isOpen: true,
      title: 'Delete Document?',
      message: 'This will permanently remove this transition record. You cannot undo this.',
      confirmText: 'Yes, Delete',
      confirmVariant: 'danger',
      onConfirm: () => {
        const updated = sessions.filter(s => s.id !== id);
        saveSessions(updated);
        toast.success('Document deleted');
        setConfirmConfig({ isOpen: false });
      }
    });
  };

  const handleReopen = (s) => {
    // If we have a stored step, use it, otherwise fall back to 'review' or 'results' based on status
    let targetStep = s.step;

    if (!targetStep) {
      targetStep = s.status === 'uploaded' ? 'review' : 'results';
    }

    // Recover the full parsed_data from the session
    const resultData = s.result; // This is the parsed_data JSON from DB

    // ─────────────────────────────────────────────────────
    // DIAGNOSTIC LOGGING — visible in browser DevTools console
    // ─────────────────────────────────────────────────────
    console.group(`[handleReopen] Session ID: ${s.id} | Step: ${targetStep}`);
    // console.log('Full session object (s):', JSON.parse(JSON.stringify(s)));
    // console.log('uploadedFileRef.updated_resume_url:', s.uploadedFileRef?.updated_resume_url);
    // console.log('resultData (parsed_data from DB):', resultData);
    // console.log('resultData.downloadUrl:', resultData?.downloadUrl);
    // console.log('resultData.name:', resultData?.name);
    // console.log('resultData.experience?.length:', resultData?.experience?.length);
    console.groupEnd();

    // ─── FIX: Reconstruct synthesizedResult so PreviewStep always gets a valid result ───
    // The `save-progress` backend deletes the `result` key from parsed_data, so we must
    // rebuild it here from the top-level DB columns and flat parsed_data fields.
    const downloadUrl =
      s.uploadedFileRef?.updated_resume_url ||
      resultData?.downloadUrl ||
      resultData?.result?.downloadUrl ||
      '';

    // console.log('[handleReopen] Resolved downloadUrl:', downloadUrl || '(EMPTY — preview will be blank without fallback)');

    const synthesizedResult = (targetStep === 'preview' || targetStep === 'results')
      ? {
          id: s.id,
          resume_id: s.id,
          downloadUrl,
          resumeData: {
            name: resultData?.name,
            email: resultData?.email,
            phone: resultData?.phone,
            location: resultData?.location,
            linkedin: resultData?.linkedin,
            github: resultData?.github,
            website: resultData?.website,
            summary: resultData?.summary,
            experience: resultData?.experience,
            education: resultData?.education,
            skills: resultData?.skills,
            projects: resultData?.projects || resultData?.leadership,
            leadership: resultData?.leadership,
            certifications: resultData?.certifications,
            interests: resultData?.interests,
            awards: resultData?.awards,
            customSections: resultData?.customSections,
          },
          fileName: downloadUrl ? downloadUrl.split('/').pop() : 'resume.pdf',
        }
      : null;

    // console.log('[handleReopen] synthesizedResult.downloadUrl:', synthesizedResult?.downloadUrl);
    // console.log('[handleReopen] synthesizedResult.resumeData.name:', synthesizedResult?.resumeData?.name);

    const recoveredState = {
      ...resultData,
      result: synthesizedResult,                           // ← KEY FIX: always set result
      targetRole: s.targetRole || resultData?.targetRole || '',
      jobDescription: s.jobDescription || resultData?.jobDescription || '',
      uploadedFile: s.uploadedFileRef,
      reviewedData: resultData?.reviewedData || resultData,
      stage: s.stage || 1,
    };

    onNavigate(targetStep, recoveredState);
  };

  const handleClearHistory = () => {
    setConfirmConfig({
      isOpen: true,
      title: 'Clear All Documents?',
      message: 'Are you sure you want to clear your entire transition history?',
      confirmText: 'Clear All',
      confirmVariant: 'danger',
      onConfirm: () => {
        saveSessions([]);
        setConfirmConfig({ isOpen: false });
        toast.success('History cleared');
      }
    });
  };

  const fmtSize = (bytes) => {
    if (!bytes) return '0 KB';
    const kb = bytes / 1024;
    return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb.toFixed(0)} KB`;
  };

  const timeAgo = (date) => {
    if (!date) return 'Recently';
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + " years ago";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + " months ago";
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " days ago";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " hours ago";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + " mins ago";
    return "Just now";
  };

  const howSteps = [
    { icon: 'upload_file', cls: 'bg-blue-light text-blue', title: 'Upload', desc: 'Drop your current PDF resume' },
    { icon: 'manage_search', cls: 'bg-premium-indigo-light text-premium-indigo', title: 'Parse', desc: 'AI extracts your current skills' },
    { icon: 'fork_right', cls: 'bg-premium-teal-light text-premium-teal', title: 'Bridge', desc: 'Define your new target role' },
    { icon: 'auto_fix_high', cls: 'bg-orange-50 text-orange-600', title: 'Pivot', desc: 'AI reframes experience for the switch' },
    { icon: 'download_done', cls: 'bg-premium-green-light text-premium-green', title: 'Done', desc: 'Download your target-ready PDF' },
  ];

  const getStepLabel = (step) => {
    switch (step) {
      case 'uploaded': return 'Review Details';
      case 'review': return 'Benchmarking';
      case 'targeting-gap': return 'Bridge the Gap';
      case 'gap-analysis': return 'Reframing';
      case 'career-roadmap': return 'Strategy Chart';
      case 'experience-builder': return 'Final Preview';
      case 'preview': return 'Complete';
      default: return 'Resume Flow';
    }
  };

  return (
    <div className="bg-slate-50 text-slate-800 font-inter flex flex-col">

      {/* MAIN CONTAINER */}
      <div className="max-w-[1240px] mx-auto px-8 py-10 flex-1 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10 items-start">

          {/* ── LEFT COLUMN: TRANSITION ENGINE ── */}
          <div className="flex flex-col gap-10 min-w-0">

            {/* Hero Section */}
            <div className="animate-fade-up" style={{ animationDelay: '0.05s' }}>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 mb-4">
                <span className="msi text-blue text-[14px]">bolt</span>
                <span className="text-[11px] font-bold text-blue uppercase tracking-wider">Strategic Career Pivot</span>
              </div>
              <h1 className="text-[36px] font-extrabold text-navy tracking-[-1px] mb-3 font-montserrat leading-tight">Your Next Chapter Starts Here.</h1>
              <p className="text-[16px] text-slate-500 leading-relaxed max-w-xl">We transform your current experience into a powerful story for your target industry instantly.</p>
            </div>

            {/* Step 1: Upload */}
            <div className="animate-fade-up" style={{ animationDelay: '0.1s' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="msi text-blue text-[20px]">upload_file</span>
                  <h2 className="text-[17px] font-bold text-navy font-montserrat tracking-tight">Step 1 — Upload Your Current Resume</h2>
                </div>
                <button 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('recent-documents-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="hidden sm:flex text-[12px] font-bold text-blue hover:text-blue-600 transition-colors items-center gap-1 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100"
                >
                  <span className="msi text-[16px]">arrow_downward</span>
                  Previously Uploaded Documents
                </button>
              </div>
              <div
                className={`bg-white border-2 border-dashed rounded-[32px] p-12 text-center cursor-pointer relative overflow-hidden transition-all duration-300 shadow-sm group hover:shadow-xl hover:border-blue-500/30 ${dragOver ? 'border-blue bg-blue-light' : 'border-slate-200'}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
              >
                <input type="file" accept=".pdf" className="absolute inset-0 opacity-0 cursor-pointer w-full h-full disabled:cursor-not-allowed z-10" onChange={(e) => {
                  handleFileSelect(e.target.files[0]);
                  e.target.value = '';
                }} disabled={sessions.length >= 5 || credits === 0} />
                
                {file && !uploadedFile ? (
                  <div className="flex flex-col items-center justify-center h-full animate-fade-up relative z-20 py-2">
                    <div className="w-20 h-20 bg-blue-50 border border-blue-100 text-blue rounded-[24px] flex items-center justify-center mb-5 relative transition-transform duration-300 group-hover:scale-105 shadow-sm">
                      <span className="msi text-[40px]">description</span>
                      <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center border-4 border-white shadow-sm">
                        <span className="msi text-[16px] font-bold">check</span>
                      </div>
                    </div>
                    
                    <h3 className="text-[17px] font-bold text-navy mb-1.5 truncate max-w-xs">{file.name}</h3>
                    <p className="text-[13px] text-slate-500 font-medium mb-8 bg-slate-50 px-3 py-1 rounded-lg border border-slate-100">{fmtSize(file.size)} • Ready to analyze</p>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleUpload();
                      }}
                      disabled={uploading || sessions.length >= 5 || credits === 0}
                      className="inline-flex items-center gap-2.5 bg-blue-500 text-white rounded-2xl px-10 py-4 text-[15px] font-bold transition-all duration-300 hover:bg-[#0055DD] hover:-translate-y-1 shadow-xl shadow-blue-500/20 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:-translate-y-0"
                    >
                      {uploading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span>Extracting Profile...</span>
                        </>
                      ) : sessions.length >= 5 ? (
                        <>
                          <span className="msi text-[18px]">lock</span>
                          <span>Limit Reached</span>
                        </>
                      ) : credits === 0 ? (
                        <>
                          <span className="msi text-[18px]">lock</span>
                          <span>No Credits</span>
                        </>
                      ) : (
                        <>
                          <span className="msi text-[18px]">auto_awesome</span>
                          <span>Analyze Resume</span>
                        </>
                      )}
                    </button>
                    
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        document.querySelector('input[type="file"]').click();
                      }}
                      className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-slate-200 text-[13px] font-bold text-slate-500 hover:border-blue-500 hover:text-blue-600 transition-all bg-white shadow-sm"
                    >
                      <span className="msi text-[18px]">swap_vert</span>
                      Change File
                    </button>
                  </div>
                ) : (
                  <div className="relative z-0 pointer-events-none">
                    <div className="w-[80px] h-[80px] rounded-3xl bg-blue-light border border-blue-100 flex items-center justify-center mx-auto mb-6 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3 shadow-md">
                      <span className="msi text-blue text-[36px]">{sessions.length >= 5 || credits === 0 ? 'lock' : 'cloud_upload'}</span>
                    </div>
                    <div className="text-[19px] font-bold text-navy mb-2 font-montserrat">{sessions.length >= 5 ? 'Limit Reached' : credits === 0 ? 'Insufficient Credits' : 'Drop to Start Your Pivot'}</div>
                    <div className="text-[14px] text-slate-500 mb-6 font-medium">{sessions.length >= 5 ? 'Delete an older document to upload a new one' : credits === 0 ? 'You need credits to upload a resume' : 'We\'ll extract your skills & experience automatically'}</div>
                    <button className="inline-flex items-center gap-2.5 bg-blue-500 text-white rounded-2xl px-10 py-4 text-[15px] font-bold transition-all duration-200 hover:bg-navy hover:-translate-y-1 shadow-2xl shadow-blue-500/30 disabled:opacity-50 disabled:hover:-translate-y-0 pointer-events-auto" onClick={() => sessions.length < 5 && credits > 0 && document.querySelector('input[type="file"]').click()} disabled={sessions.length >= 5 || credits === 0} type="button">
                      <span className="msi text-[18px]">{sessions.length >= 5 || credits === 0 ? 'lock' : 'add_circle'}</span>
                      Select PDF Resume
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Bridge Framework */}
            <div className="animate-fade-up" style={{ animationDelay: '0.15s' }}>
              <div className="bg-white border border-slate-200 rounded-[32px] p-8 shadow-sm">
                <h3 className="text-[12px] font-bold text-slate-400 uppercase tracking-[2.5px] text-center mb-8 font-montserrat">The Career Bridge Framework</h3>
                <div className="flex flex-col md:flex-row gap-6 items-center justify-between px-4">
                  {howSteps.map((step, i) => (
                    <React.Fragment key={step.title}>
                      <div className="flex flex-col items-center text-center max-w-[120px] group">
                        <div className={`w-[52px] h-[52px] rounded-2xl flex items-center justify-center mb-3 transition-transform group-hover:-translate-y-1 ${step.cls}`}>
                          <span className="msi text-[24px]">{step.icon}</span>
                        </div>
                        <h4 className="text-[13px] font-bold text-navy mb-1 font-montserrat">{step.title}</h4>
                        <p className="text-[11px] text-slate-400 leading-tight font-medium">{step.desc}</p>
                      </div>
                      {i < howSteps.length - 1 && (
                        <div className="hidden md:flex items-center text-slate-200">
                          <span className="msi text-[20px]">east</span>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent Documents */}
            <div id="recent-documents-section" className="animate-fade-up" style={{ animationDelay: '0.2s' }}>
              <div className="flex items-center justify-between mb-5 px-2">
                <div className="flex items-center gap-2">
                  <span className="msi text-blue text-[20px]">history</span>
                  <h2 className="text-[17px] font-bold text-navy font-montserrat tracking-tight">Step 2 — Recent Documents</h2>
                </div>
                {sessions.length > 0 && (
                  <button className="text-[11px] font-bold text-slate-400 hover:text-red-500 transition-colors uppercase tracking-widest" onClick={handleClearHistory}>Clear All</button>
                )}
              </div>

              <div className="bg-white border border-slate-200 rounded-[28px] overflow-hidden shadow-sm">
                {sessions.length === 0 ? (
                  <div className="py-20 text-center">
                    <div className="w-14 h-14 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-100">
                      <span className="msi text-slate-200 text-[28px]">folder_off</span>
                    </div>
                    <p className="text-[14px] text-slate-400 font-medium font-montserrat">No transition documents yet.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50/50 border-b border-slate-100">
                        <tr>
                          <th className="p-5 text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">Date & Time</th>
                          <th className="p-5 text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">Document Name</th>
                          <th className="p-5 text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">Target Role</th>
                          <th className="p-5 text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">Status</th>
                          <th className="p-5 text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sessions.map((s) => (
                          <tr key={s.id} className="hover:bg-slate-50/80 transition-all border-b border-slate-50 last:border-0">
                            <td className="p-5 text-center">
                              <div className="text-[12px] font-bold text-navy">{new Date(s.uploadedAt).toLocaleDateString()}</div>
                              <div className="text-[10px] text-slate-400 font-medium mt-0.5">{new Date(s.uploadedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                            </td>
                            <td className="p-5 text-center">
                              <div className="flex items-center justify-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center border border-red-100">
                                  <span className="msi text-[20px]">description</span>
                                </div>
                                <div className="min-w-0">
                                  <div className="text-[13px] font-bold text-navy truncate max-w-[150px]">{s.filename}</div>
                                </div>
                              </div>
                            </td>
                            <td className="p-5 text-center">
                              <span className="text-[12px] font-bold text-navy truncate max-w-[120px] block">{s.targetRole || 'In Progress'}</span>
                            </td>
                            <td className="p-5 text-center">
                              <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-lg ${s.step === 'preview' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue'}`}>
                                <div className={`w-1.5 h-1.5 rounded-full ${s.step === 'preview' ? 'bg-green-600' : 'bg-blue-500 animate-pulse'}`} />
                                {s.step === 'preview' ? 'Pivot Complete' : 'In Progress'}
                              </span>
                            </td>
                            <td className="p-5 text-center">
                              <div className="flex items-center justify-center gap-2">
                                {/* <button onClick={() => handleDeleteSession(s.id)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-red-50 text-red-500 border border-red-100 hover:bg-red-500 hover:text-white transition-all shadow-sm">
                                  <span className="msi text-[18px]">delete</span>
                                </button> */}
                                <button onClick={() => handleReopen(s)} className="inline-flex items-center gap-1.5 bg-navy text-white rounded-xl px-5 py-2.5 text-[12px] font-bold transition-all hover:bg-blue-500 shadow-lg shadow-navy/10 group">
                                  <span>Resume</span>
                                  <span className="msi text-[16px] group-hover:translate-x-0.5 transition-transform">arrow_forward</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── RIGHT COLUMN: SIDEBAR ── */}
          <div className="flex flex-col gap-6 animate-fade-up" style={{ animationDelay: '0.15s' }}>

            {/* Credit Status Card */}
            <div className="bg-navy rounded-[28px] p-6 shadow-2xl relative overflow-hidden group border border-navy">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl -mr-16 -mt-16 transition-transform group-hover:scale-125" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <span className="msi text-blue-500 text-[20px]">account_balance_wallet</span>
                    <h3 className="text-[14px] font-bold text-white font-montserrat">Credit Status</h3>
                  </div>
                  <div className="px-2 py-1 bg-white rounded-lg backdrop-blur-md">
                    <span className="text-[10px] font-black text-blue-500 uppercase">Cap: 5 Resumes</span>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex justify-between items-end mb-2">
                    <span className="text-[28px] font-black text-white font-montserrat leading-none">{credits}</span>
                    <span className="text-[13px] font-bold text-white/40 font-montserrat mb-1">/ 100 Credits</span>
                  </div>
                  <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all duration-1000"
                      style={{ width: `${credits}%` }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2">
                  <div className="bg-white/5 rounded-2xl p-4 border border-white/5 backdrop-blur-sm flex items-center justify-between transition-all hover:bg-white/10 group/item">
                    <div>
                      <div className="text-[20px] font-black text-white font-montserrat leading-none mb-1">{Math.floor(credits / 20)}</div>
                      <div className="text-[9px] text-white/40 font-bold uppercase tracking-wider">Resumes Estimated</div>
                    </div>
                    <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center transition-transform group-hover/item:scale-110">
                      <span className="msi text-white/20 text-[20px]">description</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3.5 mt-6 pt-6 border-t border-white/10">
                  <div className="flex justify-between items-center text-[13px] animate-fade-up" style={{ animationDelay: '0.2s' }}>
                    <span className="text-slate-300 font-semibold font-inter">Gap Analysis</span>
                    <span className="font-extrabold text-neutral-300 font-montserrat">5 Credits</span>
                  </div>
                  <div className="flex justify-between items-center text-[13px] animate-fade-up" style={{ animationDelay: '0.25s' }}>
                    <span className="text-slate-300 font-semibold font-inter">Resume Creation</span>
                    <span className="font-extrabold text-neutral-300 font-montserrat">10 Credits</span>
                  </div>
                  <div className="flex justify-between items-center text-[13px] animate-fade-up" style={{ animationDelay: '0.3s' }}>
                    <span className="text-slate-300 font-semibold font-inter">ATS Report</span>
                    <span className="font-extrabold text-neutral-300 font-montserrat">5 Credits</span>
                  </div>
                  <div className="mt-3 flex items-start gap-3 text-[11px] text-slate-300/80 leading-relaxed bg-white/10 p-4 rounded-2xl border border-white/10 animate-fade-up" style={{ animationDelay: '0.35s' }}>
                    <span className="msi text-[18px] text-amber-400 shrink-0">info</span>
                    <span className="font-medium">Resumes {'>'} 10 pages incur a +10 credit surcharge during analysis.</span>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Strategic Insight */}
            <div className="bg-gradient-to-br from-indigo-600 to-blue-700 rounded-[28px] p-7 shadow-xl relative overflow-hidden text-white">
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-white/10 rounded-full blur-xl" />
              <div className="flex items-center gap-2 mb-3">
                <span className="msi text-[20px] text-blue-200">psychology</span>
                <span className="text-[11px] font-bold uppercase tracking-[1.5px] text-blue-100">Pivot Strategy</span>
              </div>
              <p className="text-[13px] leading-relaxed font-inter font-medium opacity-90">
                Strategic Tip: Focus on <strong>Transferable Outcomes</strong> rather than just job title duties. This helps Bridge AI frame your experience for new industries.
              </p>
            </div>

            {/* What we support */}
            <div className="bg-white border border-slate-200 rounded-[28px] p-6 shadow-sm">
              <h3 className="text-[14px] font-bold text-navy mb-4 font-montserrat tracking-tight">Pivot Capabilities</h3>
              <div className="flex flex-col gap-4">
                {[
                  { icon: 'auto_awesome', text: 'AI Gap Analysis', col: 'text-blue' },
                  { icon: 'sync_alt', text: 'Skill Reframing', col: 'text-premium-teal' },
                  { icon: 'spellcheck', text: 'Industry Language', col: 'text-premium-amber' },
                  { icon: 'description', text: 'ATS Optimized PDF', col: 'text-red-500' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3 group">
                    <div className={`w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center shrink-0 border border-slate-100 group-hover:scale-110 transition-transform`}>
                      <span className={`msi text-[16px] ${item.col}`}>{item.icon}</span>
                    </div>
                    <span className="text-[12px] text-slate-700 font-bold font-inter group-hover:text-navy transition-colors">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
      <ConfirmModal
        {...confirmConfig}
        onCancel={() => setConfirmConfig({ isOpen: false })}
      />
    </div>
  );
};

export default DashboardStep;
